/// <reference types="./types/webpack" />

// Extend Window interface for ECGrid globals
declare global {
  interface Window {
    __ECGridLoading?: boolean;
    ECGridReactComponents?: any;
  }
}

// CRITICAL: This code MUST execute when the bundle loads
// Force execution by making it top-level and non-modular
if (typeof window !== 'undefined') {
  window.__ECGridLoading = true;
}

import React from 'react';
import ReactDOM from 'react-dom/client';

// Initialize i18n before anything else (uses embedded translations from _Layout.cshtml)
import './i18n/config';

// Initialize CQRS application layer (register handlers)
import { initializeApplication } from './application';
import { logger } from './application/core';
import { apiClient } from './infrastructure/api/client/apiClient';
initializeApplication(apiClient);

// Import global CSS for shadcn/ui
import './globals.css';

// Import Web Components system
import { initializeWebComponents } from './web-components';

// Import SIMPLE Web Components system (for debugging)
import { registerSimpleComponent } from './web-components-simple';

// Import components for backward compatibility exports
import * as allComponents from './components';

// Export components for use in MVC views (backward compatibility)
const ECGridReactComponents = {
  ...allComponents,

  // Utility function to render React components in MVC views (legacy API)
  render: (component: React.ComponentType<any>, props: any, containerId: string): void => {
    const container = document.getElementById(containerId);
    if (container) {
      const root = ReactDOM.createRoot(container);
      root.render(React.createElement(component, props));
    }
  },

  // Expose simple registration for testing
  registerSimple: registerSimpleComponent,
};

// Initialize Web Components system and auto-register all components
// SYNCHRONOUS initialization - must run immediately when bundle loads
try {
  initializeWebComponents();

  // Register SIMPLE version for debugging - with different tag name
  if (allComponents.EcgridPackageTestPage) {
    registerSimpleComponent('react-ecgrid-simple', allComponents.EcgridPackageTestPage);
  }

  // Register DeveloperRegistrationPage component
  if (allComponents.DeveloperRegistrationPage) {
    registerSimpleComponent(
      'react-developer-registration',
      allComponents.DeveloperRegistrationPage
    );
  }

  // Register DeveloperWelcomePage component
  if (allComponents.DeveloperWelcomePage) {
    registerSimpleComponent('react-developer-welcome', allComponents.DeveloperWelcomePage);
  }

  // Register PlanUpgradeSuccessPage component
  if (allComponents.PlanUpgradeSuccessPage) {
    registerSimpleComponent('react-plan-upgrade-success', allComponents.PlanUpgradeSuccessPage);
  }

  // Register DemoWelcomePage component
  if (allComponents.DemoWelcomePage) {
    registerSimpleComponent('react-demo-welcome', allComponents.DemoWelcomePage);
  }

  // Register DeveloperRegistrationErrorPage component
  if (allComponents.DeveloperRegistrationErrorPage) {
    registerSimpleComponent(
      'react-developer-registration-error',
      allComponents.DeveloperRegistrationErrorPage
    );
  }

  // Register DeveloperPlanPage component (Sales Support)
  if (allComponents.DeveloperPlanPage) {
    registerSimpleComponent('react-developer-plan', allComponents.DeveloperPlanPage);
  }

  // Register ManageSubscriptionPage component
  if (allComponents.ManageSubscriptionPage) {
    registerSimpleComponent('react-manage-subscription', allComponents.ManageSubscriptionPage);
  }

  // Register UserSettingsPage component
  if (allComponents.UserSettingsPage) {
    registerSimpleComponent('react-user-settings', allComponents.UserSettingsPage);
  }

  // Register TermsPage (route: /terms)
  if (allComponents.TermsPage) {
    registerSimpleComponent('react-terms', allComponents.TermsPage);
  }

  // Register ChangelogPage (route: /change-log)
  if (allComponents.ChangelogPage) {
    registerSimpleComponent('react-change-log', allComponents.ChangelogPage);
  }
} catch (error) {
  logger.error('ECGrid', 'Failed to initialize Web Components:', error);
}

// Make this the default export so webpack assigns it correctly to window.ECGridReactComponents
export default ECGridReactComponents;

// Also export individual modules
export * from './components';
export * from './types';
export * from './utils';

// FORCE INITIALIZATION: Ensure code runs even if module isn't imported
if (typeof window !== 'undefined') {
  if (!window.ECGridReactComponents) {
    window.ECGridReactComponents = ECGridReactComponents;
  }
}

// Hot Module Replacement (HMR) support for development
if (module.hot) {
  module.hot.accept(['./components', './types', './utils'], async () => {
    // Re-export the updated modules
    const updatedComponents = await import('./components');
    Object.assign(ECGridReactComponents, updatedComponents);
  });

  module.hot.accept();
}
